import React from 'react';
import { Box, BoxProps, alpha, useTheme } from '@mui/material';

interface IconContainerProps extends BoxProps {
  color?: 'primary' | 'secondary' | 'success' | 'warning' | 'error' | 'info';
  size?: 'small' | 'medium' | 'large';
  variant?: 'filled' | 'outlined' | 'soft';
}

const IconContainer: React.FC<IconContainerProps> = ({
  children,
  color = 'primary',
  size = 'medium',
  variant = 'soft',
  sx,
  ...props
}) => {
  const theme = useTheme();

  const getSizeStyles = () => {
    switch (size) {
      case 'small':
        return { width: 32, height: 32 };
      case 'large':
        return { width: 64, height: 64 };
      default:
        return { width: 48, height: 48 };
    }
  };

  const getVariantStyles = () => {
    const colorPalette = theme.palette[color];

    switch (variant) {
      case 'filled':
        return {
          background: colorPalette.main,
          color: colorPalette.contrastText,
        };
      case 'outlined':
        return {
          background: '#ffffff',
          border: `2px solid ${colorPalette.main}`,
          color: colorPalette.main,
        };
      default: // soft
        return {
          background: alpha(colorPalette.main, 0.1),
          color: colorPalette.main,
        };
    }
  };

  const getIconSize = () => {
    if (size === 'small') return '1.2rem';
    if (size === 'large') return '2rem';
    return '1.5rem';
  };

  return (
    <Box
      sx={{
        ...getSizeStyles(),
        borderRadius: '8px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        transition: 'transform 0.2s ease',
        '&:hover': {
          transform: 'scale(1.05)',
        },
        '& .MuiSvgIcon-root': {
          fontSize: getIconSize(),
        },
        ...getVariantStyles(),
        ...sx
      }}
      {...props}
    >
      {children}
    </Box>
  );
};

export default IconContainer;
